import { IconButton, InputAdornment, OutlinedInput, Tooltip, Typography } from '@mui/material'
import { ErrorRounded, InfoRounded } from "@mui/icons-material";
import * as React from 'react';
import {useEffect, useState} from 'react';
import { useInvoiceRepository } from "../../../api/repositories/InvoiceRepository";
import { ID_ACCOUNTED, ID_SENT, ID_TO_SEND } from '../constants/InvoicesStatuses';
import { InvoiceInsightItem } from '../constants/InvoicesModels';
import {ID_ASSISTED_DEBIT_NOTE, ID_CREDIT_NOTE, ID_DEBIT_NOTE, ID_INVOICE} from "../constants/InvoicesTypes";
import { useTranslation } from "react-i18next";
import InvoiceSimpleTable, { SimpleColumnDef } from "../../../component/InvoiceSimpleTable";
import InvoiceInsightModal from "../modals/InvoiceInsightModal";
import { formatCurrency, formatDate, formatNumber, getCurrencySymbol } from "../../../utils/FormatUtils";
import { useHasScope } from '../../../../../config/globalConfig';
import { VIEW_RECEIVABLE_INSIGHT } from '../../../utils/FinanceScope';

export interface ModifiedItem {
    parentId: string;
    quantity?: number;
    amount?: number;
}

export interface InvoiceItemsProps {
    invoice: any,
    parentInvoice?: any,
    originalInvoice?: any,
    preview: boolean,
    editable: boolean,
    editableType?: string,
    modifiedItems?: ModifiedItem[],
    setModifiedItems?: any
    setError?: any,
    setSuccess?: any,
    debitNoteAssistedValues?: any
}

const InvoiceItems = (props: InvoiceItemsProps) => {

    const { t } = useTranslation('finance', { keyPrefix: 'invoices' });
    const { getInvoicesByParentId } = useInvoiceRepository();
    const hasScope = useHasScope;
    const [insightModalOpen, setInsightModalOpen] = useState(false);
    const [insightModalItem, setInsightModalItem] = useState<InvoiceInsightItem | undefined>(undefined);
    const [insightModalInvoice, setInsightModalInvoice] = useState<InvoiceInsightItem | undefined>(undefined);
    const [insightModalHistory, setInsightModalHistory] = useState<InvoiceInsightItem[]>([]);
    const [insightModalResidualQuantity, setInsightModalResidualQuantity] = useState<number>(0);
    const [insightModalResidualAmount, setInsightModalResidualAmount] = useState<number>(0);

    const [itemsHistory, setItemsHistory] = useState<any[]>([]);

    const handleChangeQuantity = (data: any, value: string, itemId: string) => {
        if (value.match(/^\s*-?[1-9]\d*(\.\d{0,2})?\s*$/)) {
            let numberValue: number = parseFloat(value);
            if (props.setError !== undefined || props.setSuccess !== undefined) {
                if (checkQuantityErrorValue(data, numberValue)) {
                    props.setError();
                } else {
                    props.setSuccess();
                }
            }


            if (props.modifiedItems?.find((mod: any) => mod.parentId === itemId) === undefined) {
                props.setModifiedItems((prev: any) => [...prev, {
                    parentId: itemId,
                    quantity: numberValue
                }]);
            } else {
                props.setModifiedItems((prev: any) => {
                    return prev.map((item: any) => item.parentId === itemId ? { ...item, quantity: numberValue } : { ...item })
                });
            }
        }
        if (value === '' || parseInt(value) <= 0) {
            props.setModifiedItems((prev: any) => {
                return prev.filter((item: any) => item.parentId !== itemId)
            });
        }
    }

    const handleChangeAmount = (data: any, value: string, itemId: string) => {
        if (value) value = value.replace(',', '.');
        if (value.match(/^\s*-?(0|[1-9]\d*)(\.\d{0,2})?\s*$/)) {
            let numberValue: number = parseFloat(value);

            if (props.setError !== undefined || props.setSuccess !== undefined) {
                if (checkAmountErrorValue(data, numberValue)) {
                    props.setError();
                } else {
                    props.setSuccess();
                }
            }
            if (props.modifiedItems?.filter((mod) => mod.parentId === itemId).length === 0) {
                props.setModifiedItems((prev: any) => [
                    ...prev,
                    {
                        parentId: itemId,
                        amount: numberValue
                    },
                ]);
            } else {
                props.setModifiedItems((prev: any) => {
                    return prev.map((item: any) => item.parentId === itemId ? { ...item, amount: numberValue } : { ...item })
                });
            }
        }
        if (value === '' || parseInt(value) < 0) {
            props.setModifiedItems((prev: any) => {
                return prev.filter((item: any) => item.parentId !== itemId)
            });
        }
    }

    const checkQuantityError = (data: InvoiceInsightItem) => {
        if (props.editableType !== ID_CREDIT_NOTE) {
            return false;
        }
        let value: number = props.modifiedItems?.filter((mod) => mod.parentId === data.id && mod.quantity)[0]?.quantity || 0;

        return value > getResidual(data).quantity;
    }

    const checkQuantityErrorValue = (data: InvoiceInsightItem, value: number) => {
        if (props.editableType !== ID_CREDIT_NOTE) {
            return false;
        }

        return value > getResidual(data).quantity;
    }

    const checkAmountError = (data: InvoiceInsightItem) => {
        if (props.editableType !== ID_CREDIT_NOTE) {
            return false;
        }
        let value: number = props.modifiedItems?.filter((mod) => mod.parentId === data.id && mod.amount)[0]?.amount || 0;
        let residual = getResidual(data).totalNetPrice;

        return Math.trunc(value * 100) > residual;
    }

    const checkAmountErrorValue = (data: InvoiceInsightItem, value: number) => {
        if (props.editableType !== ID_CREDIT_NOTE) {
            return false;
        }
        let residual = getResidual(data).totalNetPrice;
        return Math.trunc(value * 100) > residual;
    }

    const getResidual = (data: InvoiceInsightItem) => {
        let invoice = getParentInvoice(props.parentInvoice ? data.parentId : data.id);
        let history = getItemHistory(data.id, props.parentInvoice ? data.parentId : data.id);
        return getItemResidual(invoice, history);
    }

    const sign = props.editableType === ID_CREDIT_NOTE ? '-' : '';

    const columnDefs: SimpleColumnDef[] = [
        {
            field: 'id', headerName: '', width: '50px',
            valueFormatter: (data: any) => {
                if (!props.preview) {
                    return {
                        component: () => {
                            if(!hasScope(VIEW_RECEIVABLE_INSIGHT)){
                                return null;
                            }
                            return (
                                    <IconButton onClick={() => {
                                        let invoice = getParentInvoice(props.parentInvoice ? data.parentInvoiceItemId : data.id);
                                        let history = getItemHistory(data.id, props.parentInvoice ? data.parentInvoiceItemId : data.id);
                                        let residual = getItemResidual(invoice, history);
  
                                        setInsightModalItem(invoice);
                                        setInsightModalInvoice(invoice);
                                        setInsightModalHistory(history);
                                        setInsightModalResidualQuantity(residual.quantity);
                                        setInsightModalResidualAmount(residual.totalNetPrice);
                                        setInsightModalOpen(true);
                                    }}>
                                        <InfoRounded htmlColor="#C8017D"/>
                                    </IconButton>);
                        }
                    }
                }
            }
        },
        { field: 'serviceDescription', headerName: t('preview.tables.items.description'), width: '30%' },
        { field: 'id', headerName: t('preview.tables.items.reference'), width: '10%' },
        {
            field: 'quantity', headerName: t('preview.tables.items.quantity'), width: '100px',
            valueFormatter: (data: any) => {
                return {
                    component: () => {
                        if (props.editable) {
                            return (
                                    <OutlinedInput
                                            inputProps={{
                                                style: { minWidth: '5ch' }
                                            }}
                                            size={'small'}
                                            name="quantity"
                                            type={'number'}
                                            disabled={props.modifiedItems?.filter((mod) => mod.parentId === data.id && mod.amount)[0]?.amount !== undefined || data.quantity === undefined || data.quantity === null}
                                            placeholder={'0'}
                                            value={
                                                props.modifiedItems?.filter((mod) => mod.parentId === data.id && mod.quantity)[0]?.quantity || ""
                                            }
                                            endAdornment={
                                                <InputAdornment position={'end'}>
                                                    {sign}
                                                    <Tooltip title={t('preview.tables.items.value-exceed-label')} sx={{ display: checkQuantityError(data) ? 'block' : 'none' }}>
                                                        <ErrorRounded/>
                                                    </Tooltip>
                                                </InputAdornment>
                                            }
                                            error={checkQuantityError(data)}
                                            onChange={(e) => {
                                                handleChangeQuantity(data, e.target.value, data.id);
                                            }}
                                    />
                            );
                        } else {
                           
                            return data.quantity != null ? (sign + formatNumber(data.quantity)) : "";
                        }
                    }
                }
            }
        },
        { field: 'serviceItem.um', headerName: t('preview.tables.items.unit-measure'), width: '10%' },
        {
            field: 'unitPrice', headerName: t('preview.tables.items.unit-price'), width: '10%', cellStyle: 'right',
            valueFormatter: (data: any) => {
                return {
                    component: () => {
                        return (
                            <Typography align={'right'}>
                                {formatCurrency(data.unitPrice, props.invoice.currency, 0.01)}
                            </Typography>
                        )
                    }
                }
            }
        },
        {
            field: 'totalNetPrice', headerName: t('preview.tables.items.amount'), width: '100px', cellStyle: 'right',
            valueFormatter: (data: any) => {
                return {
                    component: () => {
                        if (props.editable) {
                            return (
                                    <OutlinedInput
                                            inputProps={{
                                                style: { minWidth: '5ch', textAlign:'right' },
                                            }}
                                            size={'small'}
                                            name="amount"
                                            type={'number'}
                                            disabled={props.modifiedItems?.filter((mod) => mod.parentId === data.id && mod.quantity)[0]?.quantity !== undefined}
                                            startAdornment={<InputAdornment position="start">{getCurrencySymbol(props.invoice.currency)}</InputAdornment>}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    {sign}
                                                    <Tooltip title={t('preview.tables.items.value-exceed-label')} sx={{ display: checkAmountError(data) ? 'block' : 'none' }}>
                                                        <ErrorRounded/>
                                                    </Tooltip>
                                                </InputAdornment>
                                            }
                                            placeholder={'0'}
                                            value={
                                                    props.modifiedItems?.filter((mod) => mod.parentId === data.id && mod.amount)[0]?.amount
                                            }
                                            error={checkAmountError(data)}
                                            onChange={(e) => handleChangeAmount(data, e.target.value, data.id)}
                                    />
                            );
                        } else {
                            return (
                                <Typography align={'right'}>
                                    {(sign + formatCurrency(data.totalNetPrice, props.invoice.currency, 0.01)) + (data.totalDiscount !== 0 && data.totalDiscount !== undefined ? ` (-${formatCurrency(data.totalDiscount, props.invoice.currency, 0.01)})` : "")}
                                </Typography>
                            )
                        }
                    }
                }
            }
        },
        { field: 'taxType.value', headerName: t('preview.tables.items.vat'), width: '10%', cellStyle: 'right',
            valueFormatter: (data: any) => {
                return {
                    component: () => {
                        return (
                            <Typography align={'right'}>
                                {data.taxType.value}
                            </Typography>
                        )
                    }
                }
            }
        },
    ];


    /**
     * Finds the history mapped to InvoiceInsightItem[] by item's id.
     *
     * @param id the item's id to get history for.
     * @param parentId
     */
    const getItemHistory = (id: string, parentId: string | undefined): InvoiceInsightItem[] => {
        let history = itemsHistory?.find(itemHistory => itemHistory.id === id);
        return history?.history?.map((it: any) => {
            let quantity = it.items.find((item: any) => item.parentInvoiceItemId === parentId).quantity;
            let amount = it.items.find((item: any) => item.parentInvoiceItemId === parentId).totalNetPrice;

            return {
                id: it.id,
                type: it.documentType,
                name: `${it.invoiceNumber} ${t('preview.insight-modal.of-date')} ${formatDate(it.invoiceDate)}`,
                status: it.status,
                quantity: quantity,
                amount: amount,
                currency: props.invoice.currency,
                parentId: it.parentInvoiceItemId
            }
        })
    }

    /**
     * Calculates the residual amount and quantity for a specific item based on the original invoice and the respective history.
     *
     * @param originalInvoice the original invoice mapped to InvoiceInsightItem.
     * @param history the history of the item mapped to InvoiceInsightItem[].
     */
    const getItemResidual = (originalInvoice: InvoiceInsightItem, history: InvoiceInsightItem[]): { quantity: number, totalNetPrice: number } => {
        let quantity = originalInvoice.quantity - (history?.map(it => it.quantity).reduce((prev, curr) => prev + Math.abs(curr), 0));
        let amount = originalInvoice.amount - (history?.map(it => it.amount).reduce((prev, curr) => prev + Math.abs(curr), 0));
        return ({ quantity: Number.isNaN(quantity) ? 0 : quantity, totalNetPrice: amount });
    }

    /**
     * Creates an InvoiceInsightItem reference based on the original invoice where notes started from.
     * The original invoice is considered the opened invoice when the type is INVOICE, the parentInvoice is used otherwise.
     */
    const getParentInvoice = (id: string | undefined) => {
        let originalInvoice = props.originalInvoice ? props.originalInvoice : props.invoice;
        let currentInvoice: any = props.parentInvoice ? props.parentInvoice : originalInvoice;

        return {
            id: currentInvoice.id,
            name: `${currentInvoice.invoiceNumber} ${t('preview.insight-modal.of-date')} ${formatDate(currentInvoice.invoiceDate)}`,
            type: currentInvoice.documentType,
            status: currentInvoice.status,
            quantity: currentInvoice.items.find((it: any) => it.id === id)?.quantity || 0,
            amount: currentInvoice.items.find((it: any) => it.id === id)?.totalNetPrice || 0,
            currency: currentInvoice.currency,
            serviceDescription: currentInvoice.items.find((it: any) => it.id === id)?.serviceDescription,
            templateRenderedContent: currentInvoice.items.find((it: any) => it.id === id)?.templateRenderedContent,
            ntwRaData: currentInvoice.items.find((it: any) => it.id === id)?.ntwRaData || '',
        };
    }

    /**
     * The data to show depends on preview state.
     * If in preview, the items to show are the ones that are also modified.
     * If not in preview, the items to show correspond to all items of the invoice.
     */
    function getData() {
        if (props.preview) {
            return props.invoice.items.filter((it: any) => {
                return props.modifiedItems?.find((mit: any) => mit.parentId === it.id)
            })
        } else {
            return props.invoice.items;
        }
    }


    /**
     * Requests all the invoices filtered by parentId, type and status. Then it creates the history for each item of the current viewing invoice.
     * In case of INVOICE and DEBIT_NOTE (since those types can be parents of new invoices), the history is composed by all credit notes with an item which parentId matches the current invoice items id.
     * In case of CREDIT_NOTE, the history is composed by all credit notes with an item which parent id matches the current invoice items parent id.
     */
    useEffect(() => {
        let currentInvoice = props.originalInvoice ? props.originalInvoice : props.invoice;
        getInvoicesByParentId(currentInvoice.documentType === ID_CREDIT_NOTE ? currentInvoice.parentId : currentInvoice.id, [ID_CREDIT_NOTE], [ID_TO_SEND, ID_SENT, ID_ACCOUNTED], 0, 1000, null, null, currentInvoice.legalEntity, currentInvoice.delaer).then((data) => {
            let historyClone = [...itemsHistory];
            props.invoice.items.forEach((i: any) => {
                historyClone.push(
                        {
                            id: i.id,
                            history: data.result.content.filter((invoice: any) => invoice.items.find((item: any) => {
                                if (currentInvoice.documentType === ID_INVOICE || currentInvoice.documentType === ID_DEBIT_NOTE)
                                    return item.parentInvoiceItemId === i.id;
                                else {
                                    return item.parentInvoiceItemId === i.parentInvoiceItemId;
                                }
                            }))
                        }
                );
            });
            setItemsHistory(historyClone);
        });
    }, []);

    useEffect(() => {
        if (props.editableType === ID_ASSISTED_DEBIT_NOTE && props.debitNoteAssistedValues && props.debitNoteAssistedValues.length !== 0) {
            getData()
                .filter((it: any) => props.debitNoteAssistedValues.some((value: any) => value.invoiceItemId === it.id))
                .forEach((it: InvoiceInsightItem) => {
                    const newValue = props.debitNoteAssistedValues.find((value : any) => value.invoiceItemId === it.id)?.newPriceAmount ?? 0;
                    handleChangeAmount(it, formatNumber(newValue * 0.01).replace(",", "."), it.id);
                })
        }
    }, [props.debitNoteAssistedValues, props.editableType === ID_ASSISTED_DEBIT_NOTE])


    return (
            <>
                <InvoiceSimpleTable columnDefs={columnDefs} data={getData()}/>
                <InvoiceInsightModal invoice={insightModalInvoice} item={insightModalItem} history={insightModalHistory}
                                     residualQuantity={insightModalResidualQuantity} residualAmount={insightModalResidualAmount}
                                     open={insightModalOpen} onClose={() => {
                    setInsightModalOpen(false)
                }}/>
            </>
    )
}
export default InvoiceItems;